/**
 * 回收站弹窗组件 (业务模式模板)
 *
 * 管理后台通用的回收站模式：展示已删除数据列表，支持恢复/永久删除/批量操作。
 * 泛型设计，可适用于任何业务实体。
 *
 * **注意**: 此组件为业务模式模板 (Business Pattern Template)，而非纯通用 UI 组件。
 * 它耦合了特定的 API 响应格式 (`ApiResponse<PaginatedData<T>>`)，
 * 适用于后端遵循统一响应格式的管理后台项目。
 * 如果你的 API 格式不同，建议参考此组件的实现模式自行封装。
 */

import { Button, Modal, Popconfirm, Space, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useLatestRef } from '../../../hooks/useLatestRef';
import type { ApiResponse, PaginatedData } from '../../../types/api';
import { createLogger } from '../../../utils/logger';

/**
 * 回收站项的最小接口约束
 */
export interface RecycleBinItem {
  /** 记录 ID（支持 number 和 string UUID） */
  id: string | number;
  /** 删除时间（可选展示） */
  deletedAt?: string;
}

/**
 * 回收站 API 接口
 */
export interface RecycleBinApi<T extends RecycleBinItem> {
  /** 获取回收站列表 */
  list: (params: {
    pageNo: number;
    pageSize: number;
  }) => Promise<ApiResponse<PaginatedData<T>>>;
  /** 恢复单条记录 */
  restore: (id: T['id']) => Promise<ApiResponse<unknown>>;
  /** 永久删除单条记录 */
  permanentDelete: (id: T['id']) => Promise<ApiResponse<unknown>>;
  /** 批量恢复（可选） */
  batchRestore?: (ids: T['id'][]) => Promise<ApiResponse<unknown>>;
  /** 清空回收站（可选） */
  clear?: () => Promise<ApiResponse<unknown>>;
}

/**
 * RecycleBinModal 文案配置
 */
export interface RecycleBinMessages {
  /** 弹窗标题，默认 '回收站' */
  title?: string;
  /** 恢复按钮文字，默认 '恢复' */
  restoreText?: string;
  /** 删除按钮文字，默认 '删除' */
  deleteText?: string;
  /** 批量恢复按钮文字，默认 '批量恢复' */
  batchRestoreText?: string;
  /** 清空按钮文字，默认 '清空' */
  clearAllText?: string;
  /** 操作列标题，默认 '操作' */
  actionsColumnTitle?: string;
  /** 永久删除确认文字，默认 '确定永久删除吗？此操作不可撤销。' */
  permanentDeleteConfirm?: string;
  /** 清空确认文字，默认 '确定清空回收站吗？此操作不可撤销。' */
  clearConfirm?: string;
  /** 恢复成功提示，默认 '恢复成功' */
  restoreSuccess?: string;
  /** 恢复失败提示，默认 '恢复失败' */
  restoreFailed?: string;
  /** 删除成功提示，默认 '已永久删除' */
  deleteSuccess?: string;
  /** 删除失败提示，默认 '删除失败' */
  deleteFailed?: string;
  /** 批量恢复成功提示模板，使用 {count} 占位符，默认 '已恢复 {count} 条记录' */
  batchRestoreSuccess?: string;
  /** 批量恢复失败提示，默认 '批量恢复失败' */
  batchRestoreFailed?: string;
  /** 清空成功提示，默认 '回收站已清空' */
  clearSuccess?: string;
  /** 清空失败提示，默认 '清空失败' */
  clearFailed?: string;
  /** 加载失败提示，默认 '加载回收站数据失败' */
  loadFailed?: string;
  /** 总数展示模板，使用 {total} 占位符，默认 '共 {total} 条' */
  totalText?: string;
}

/** 默认文案 */
const DEFAULT_MESSAGES: Required<RecycleBinMessages> = {
  title: '回收站',
  restoreText: '恢复',
  deleteText: '删除',
  batchRestoreText: '批量恢复',
  clearAllText: '清空',
  actionsColumnTitle: '操作',
  permanentDeleteConfirm: '确定永久删除吗？此操作不可撤销。',
  clearConfirm: '确定清空回收站吗？此操作不可撤销。',
  restoreSuccess: '恢复成功',
  restoreFailed: '恢复失败',
  deleteSuccess: '已永久删除',
  deleteFailed: '删除失败',
  batchRestoreSuccess: '已恢复 {count} 条记录',
  batchRestoreFailed: '批量恢复失败',
  clearSuccess: '回收站已清空',
  clearFailed: '清空失败',
  loadFailed: '加载回收站数据失败',
  totalText: '共 {total} 条',
};

/**
 * RecycleBinModal 组件属性
 *
 * **注意**: 此组件为业务模式模板，耦合了特定的 API 响应格式 (`ApiResponse<PaginatedData<T>>`)。
 * 适用于后端遵循统一响应格式的管理后台项目。
 */
export interface RecycleBinModalProps<T extends RecycleBinItem> {
  /** 是否显示 */
  open: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 回收站 API */
  api: RecycleBinApi<T>;
  /** 表格列定义 */
  columns: ColumnsType<T>;
  /** 弹窗标题（优先于 messages.title） */
  title?: string;
  /** 成功状态码（与 ApiResponse.code 比较时自动转为字符串），默认 200 */
  successCode?: string | number;
  /** 恢复成功后的回调（用于刷新主列表） */
  onRestoreSuccess?: () => void | Promise<void>;
  /** 弹窗宽度，默认 800 */
  width?: number;
  /** 消息展示函数（必须由消费方提供） */
  showMessage?: (content: string, type: 'success' | 'error') => void;
  /** 文案配置（国际化支持） */
  messages?: Partial<RecycleBinMessages>;
}

const logger = createLogger({ prefix: 'RecycleBinModal' });

/** 未注入 showMessage 时通过 logger 输出 */
function defaultMessageHandler(content: string, type: 'success' | 'error') {
  if (type === 'error') {
    logger.error(content);
  } else {
    logger.success(content);
  }
}

/**
 * 通用回收站弹窗组件
 *
 * @example
 * ```tsx
 * <RecycleBinModal
 *   open={recycleBinOpen}
 *   onClose={() => setRecycleBinOpen(false)}
 *   api={{
 *     list: getRecycleBinList,
 *     restore: restoreItem,
 *     permanentDelete: permanentDeleteItem,
 *     batchRestore: batchRestoreItems,
 *     clear: clearRecycleBin,
 *   }}
 *   columns={[
 *     { title: '名称', dataIndex: 'name' },
 *     { title: '删除时间', dataIndex: 'deletedAt' },
 *   ]}
 *   showMessage={(content, type) => message[type](content)}
 *   onRestoreSuccess={() => actionRef.current?.reload()}
 * />
 * ```
 */
export function RecycleBinModal<T extends RecycleBinItem>({
  open,
  onClose,
  api,
  columns,
  title,
  successCode = '200',
  onRestoreSuccess,
  width = 800,
  showMessage,
  messages: messagesProp,
}: RecycleBinModalProps<T>): React.ReactElement {
  const msg = { ...DEFAULT_MESSAGES, ...messagesProp };
  const modalTitle = title ?? msg.title;
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<T['id'][]>([]);

  // 用 useLatestRef 稳定回调引用，防止重建导致 useCallback / useEffect 链式更新
  const showMessageRef = useLatestRef(showMessage ?? defaultMessageHandler);
  const onRestoreSuccessRef = useLatestRef(onRestoreSuccess);
  const apiRef = useLatestRef(api);
  const msgRef = useLatestRef(msg);

  const isSuccess = useCallback(
    (response: ApiResponse<unknown>) =>
      String(response.code) === String(successCode),
    [successCode],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRef.current.list({ pageNo, pageSize });
      if (isSuccess(response) && response.data) {
        setData(response.data.list || []);
        setTotal(response.data.total || 0);
      }
    } catch (err) {
      logger.error('Failed to load recycle bin data', err);
      showMessageRef.current(msgRef.current.loadFailed, 'error');
    } finally {
      setLoading(false);
    }
  }, [pageNo, pageSize, isSuccess, apiRef, msgRef, showMessageRef]);

  useEffect(() => {
    if (open) {
      fetchData();
      setSelectedRowKeys([]);
    }
  }, [open, fetchData]);

  const handleRestore = useCallback(
    async (id: T['id']) => {
      try {
        const response = await apiRef.current.restore(id);
        if (isSuccess(response)) {
          showMessageRef.current(msgRef.current.restoreSuccess, 'success');
          fetchData();
          onRestoreSuccessRef.current?.();
        } else {
          showMessageRef.current(
            response.msg || msgRef.current.restoreFailed,
            'error',
          );
        }
      } catch (err) {
        logger.error('Restore failed', err);
        showMessageRef.current(msgRef.current.restoreFailed, 'error');
      }
    },
    [isSuccess, fetchData, apiRef, msgRef, onRestoreSuccessRef, showMessageRef],
  );

  const handlePermanentDelete = useCallback(
    async (id: T['id']) => {
      try {
        const response = await apiRef.current.permanentDelete(id);
        if (isSuccess(response)) {
          showMessageRef.current(msgRef.current.deleteSuccess, 'success');
          fetchData();
        } else {
          showMessageRef.current(
            response.msg || msgRef.current.deleteFailed,
            'error',
          );
        }
      } catch (err) {
        logger.error('Permanent delete failed', err);
        showMessageRef.current(msgRef.current.deleteFailed, 'error');
      }
    },
    [isSuccess, fetchData, apiRef, msgRef, showMessageRef],
  );

  const handleBatchRestore = useCallback(async () => {
    if (!apiRef.current.batchRestore || selectedRowKeys.length === 0) return;
    try {
      const response = await apiRef.current.batchRestore(selectedRowKeys);
      if (isSuccess(response)) {
        showMessageRef.current(
          msgRef.current.batchRestoreSuccess.replace(
            '{count}',
            String(selectedRowKeys.length),
          ),
          'success',
        );
        setSelectedRowKeys([]);
        fetchData();
        onRestoreSuccessRef.current?.();
      } else {
        showMessageRef.current(
          response.msg || msgRef.current.batchRestoreFailed,
          'error',
        );
      }
    } catch (err) {
      logger.error('Batch restore failed', err);
      showMessageRef.current(msgRef.current.batchRestoreFailed, 'error');
    }
  }, [
    selectedRowKeys,
    isSuccess,
    fetchData,
    apiRef,
    msgRef,
    onRestoreSuccessRef,
    showMessageRef,
  ]);

  const handleClear = useCallback(async () => {
    if (!apiRef.current.clear) return;
    try {
      const response = await apiRef.current.clear();
      if (isSuccess(response)) {
        showMessageRef.current(msgRef.current.clearSuccess, 'success');
        fetchData();
      } else {
        showMessageRef.current(
          response.msg || msgRef.current.clearFailed,
          'error',
        );
      }
    } catch (err) {
      logger.error('Clear recycle bin failed', err);
      showMessageRef.current(msgRef.current.clearFailed, 'error');
    }
  }, [isSuccess, fetchData, apiRef, msgRef, showMessageRef]);

  const handleTableChange = useCallback((pagination: TablePaginationConfig) => {
    setPageNo(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 10);
  }, []);

  // 操作列
  const actionColumn: ColumnsType<T>[number] = {
    title: msg.actionsColumnTitle,
    key: 'action',
    width: 160,
    render: (_: unknown, record: T) => (
      <Space size={4}>
        <Button
          type="link"
          size="small"
          onClick={() => handleRestore(record.id)}
        >
          {msg.restoreText}
        </Button>
        <Popconfirm
          title={msg.permanentDeleteConfirm}
          onConfirm={() => handlePermanentDelete(record.id)}
        >
          <Button type="link" size="small" danger>
            {msg.deleteText}
          </Button>
        </Popconfirm>
      </Space>
    ),
  };

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      width={width}
      footer={null}
      destroyOnClose
    >
      {/* 批量操作栏 */}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Space>
          {api.batchRestore && (
            <Button
              disabled={selectedRowKeys.length === 0}
              onClick={handleBatchRestore}
            >
              {msg.batchRestoreText}{' '}
              {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
            </Button>
          )}
        </Space>
        <Space>
          {api.clear && (
            <Popconfirm title={msg.clearConfirm} onConfirm={handleClear}>
              <Button danger>{msg.clearAllText}</Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      <Table<T>
        rowKey="id"
        columns={[...columns, actionColumn]}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pageNo,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => msg.totalText.replace('{total}', String(t)),
        }}
        onChange={handleTableChange}
        rowSelection={
          api.batchRestore
            ? {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys as T['id'][]),
              }
            : undefined
        }
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  );
}
